import { defaultStrokeColor, mapActive } from "../utils/map-utils";
import { CustomSource } from "../models/CustomSource";
import { defaultStreetViewState, StreetViewState } from "../models/StreetView";
import turf from "../../shared/utils/turf";

export class StreetViewMapSource {
  map: mapboxgl.Map;

  streetViewState = defaultStreetViewState();
  strokeColor = defaultStrokeColor;

  constructor(map: mapboxgl.Map) {
    this.map = map;

    this.config();
  }

  clear() {
    if (mapActive(this.map)) {
      this.map.removeLayer(symbolLayerConfig.id);
      this.map.removeLayer(lineLayerConfig.id);
      this.map.removeSource(sourceName);
    }
  }

  private config() {
    if (mapActive(this.map) && !this.map.getSource(sourceName)) {
      this.map.addSource(sourceName, sourceConfig);
      this.map.addLayer(lineLayerConfig);
      this.map.addLayer(symbolLayerConfig);
    }
  }

  update(value: Partial<StreetViewState>) {
    this.streetViewState = { ...this.streetViewState, ...value };

    if (mapActive(this.map)) {
      const source = this.map.getSource(sourceName) as mapboxgl.GeoJSONSource;

      const features: GeoJSON.Feature[] = [];

      if (this.streetViewState.position) {
        const pointLngLat = [
          this.streetViewState.position.lng,
          this.streetViewState.position.lat,
        ];
        features.push(
          turf.point(pointLngLat, {
            heading: this.streetViewState.pov.heading,
          }),
        );

        if (this.streetViewState.nearestPointInLine) {
          features.push(
            turf.lineString([
              pointLngLat,
              this.streetViewState.nearestPointInLine,
            ]),
          );
        }
      }

      source?.setData({
        type: "FeatureCollection",
        features,
      });
    }
  }
}

const isLineString = ["==", "$type", "LineString"];
const isPoint = ["==", "$type", "Point"];

const sourceName = CustomSource.StreetView;
const sourceConfig: mapboxgl.GeoJSONSourceSpecification = {
  type: "geojson",
  promoteId: "id",
  data: {
    type: "FeatureCollection",
    features: [],
  },
};

const symbolLayerConfig: mapboxgl.SymbolLayerSpecification = {
  id: `${sourceName}-symbol`,
  type: "symbol",
  source: sourceName,
  layout: {
    "icon-image": "camera-icon",
    "icon-rotate": ["get", "heading"],
    "icon-rotation-alignment": "map",
    "icon-allow-overlap": true,
    "icon-ignore-placement": true,
  },
  filter: isPoint,
};

const lineLayerConfig: mapboxgl.LineLayerSpecification = {
  id: `${sourceName}-dashed-line`,
  source: sourceName,
  type: "line",
  layout: {
    "line-join": "round",
    "line-cap": "round",
  },
  paint: {
    "line-color": "#12D3C4",
    "line-width": 3,
    "line-dasharray": [2, 2],
  },
  filter: isLineString,
};
